import {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useState,
} from 'react';
import { alpha, Box, Stack } from '@mui/material';
import RichText, { RichTextContent } from '../../../RichText';
import useWindowHeight from '../../../../utils/useWindowHeight';
import Video, { VideoRefAttributes } from '../../../gallery/Video';

type Props = {
  description?: RichTextContent;
  playbackId: string;
  variant: 'static' | 'masonry';
  onEnd?: () => void;
  blurDataURL?: string;
  ar: number;
};

const VideoTestimonial: ForwardRefExoticComponent<
  Props & RefAttributes<VideoRefAttributes>
> = forwardRef<VideoRefAttributes, Props>(
  (
    { description, playbackId, variant, onEnd, blurDataURL, ar }: Props,
    ref,
  ) => {
    const isStatic = variant === 'static';
    const { innerHeight } = useWindowHeight();

    const [isPlaying, setIsPlaying] = useState(false);

    return (
      <Stack
        className={'tb-card-video'}
        sx={{
          boxSizing: 'content-box',
          borderRadius: { xs: 0.5, md: 0.8 },
          overflow: 'hidden',
          width: '100%',
          maxWidth: {
            xs: '100%',
            md: isStatic ? '420px' : 'auto',
          },
          maxHeight: `min(700px, ${innerHeight ? 0.7 * innerHeight + 'px' : '70svh'})`,
          position: 'relative',
          filter: isPlaying ? undefined : 'grayscale(50%)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1,
            py: 2,
            px: { xs: 2, md: 2 },
            bgcolor: alpha('#000', 0.75),
            color: 'white',
            borderRadius: 'inherit',
          }}
        >
          <RichText withTypography variant={'h5'} component={'p'}>
            {description}
          </RichText>
        </Box>
        <Video
          ref={ref}
          playbackId={playbackId}
          aspectRatio={ar}
          blurDataURL={blurDataURL}
          onEnded={onEnd}
          onPlaying={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          sx={{
            borderRadius: 'inherit',
            overflow: 'hidden',
            display: 'flex',
            maxWidth: {
              xs: '100%',
              md: isStatic ? '420px' : 'auto',
            },
            height: isStatic ? '420px' : '100%',
            aspectRatio: isStatic ? 1 : ar,
          }}
        />
      </Stack>
    );
  },
);
VideoTestimonial.displayName = 'VideoTestimonial';

export default VideoTestimonial;
